import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useBootSequence } from "../../hooks/useBootSequence";
import {
  b64ToU8,
  generateIdentity,
  identityFromStored,
  initSodium,
  type IdentityKeys,
  u8ToB64,
} from "../../lib/crypto";
import {
  apiLogin,
  apiRegister,
  apiRenameUsername,
  clearStoredJwt,
  setStoredJwt,
} from "../../lib/authApi";
import { clearWorkspaceNavSessionCache } from "../../lib/workspaceNavSessionCache";
import {
  clearEncryptedLocalCaches,
  loadIdentity,
  saveIdentity,
  setActiveUserId,
  stripIdentitySecret,
  type IdentityRow,
} from "../../lib/db";
import { clearLocalCacheKey, setLocalCacheKeyFromSecretKey } from "../../lib/localCacheKey";
import { decryptSecretKeyBackup, encryptSecretKeyBackup, randomKeySalt } from "../../lib/keyBackup";
import { unsubscribePushNotifications } from "../../lib/push";
import type { BootItem } from "../types";
import { bootReducer, initialBootState, type BootAction } from "../state/bootState";
import type { BootKey } from "../state/domainTypes";
import { normalizeUsername } from "../state/appUtils";
import { useSessionResetRegistry } from "../SessionResetContext";
import { useTheme } from "./ThemeContext";
import { pickAvatarDisplayUrl, type AvatarUrls } from "../../lib/avatarDisplay";
import { bootDoneJitterMs } from "../../lib/bootTiming";
import {
  getBootOverlayModeSnapshot,
  setBootOverlayMode,
  subscribeBootOverlayMode,
  type BootOverlayMode,
} from "../../lib/pwaBootOverlayMode";
import {
  getPwaBootUpdateSnapshot,
  subscribePwaBootUpdate,
  type PwaBootUpdateUi,
} from "../../lib/pwaBootUpdateStatus";
import { clearPostSwRestartBoot, isPostSwRestartBoot } from "../../lib/postSwReloadSession";
import { consumeLoginThemeResponse } from "../../lib/themeLoginBootstrap";
import { swUpdateBlocksBootDismiss } from "../../lib/pwaBootRegistration";

type AuthContextValue = {
  sodiumReady: boolean;
  identityRow: IdentityRow | null;
  keys: IdentityKeys | null;
  keysRef: React.MutableRefObject<IdentityKeys | null>;
  identityRowRef: React.MutableRefObject<IdentityRow | null>;
  authTab: "register" | "login";
  setAuthTab: (t: "register" | "login") => void;
  regEmail: string;
  setRegEmail: (v: string) => void;
  regPassword: string;
  setRegPassword: (v: string) => void;
  regPassword2: string;
  setRegPassword2: (v: string) => void;
  regUsername: string;
  setRegUsername: (v: string) => void;
  regInviteCode: string;
  setRegInviteCode: (v: string) => void;
  loginEmail: string;
  setLoginEmail: (v: string) => void;
  loginPassword: string;
  setLoginPassword: (v: string) => void;
  bootError: string | null;
  setBootError: (e: string | null) => void;
  renameUsernameError: string | null;
  setRenameUsernameError: (e: string | null) => void;
  myAvatarUrl: string | null;
  myAvatarUrls: AvatarUrls | null;
  setMyAvatarUrls: (u: AvatarUrls | null) => void;
  bootItems: ReturnType<typeof initialBootState>["bootItems"];
  bootVisible: boolean;
  setBoot: (key: BootKey, state: BootItem["state"], detail?: string) => Promise<void>;
  hideBoot: () => void;
  bootDispatch: React.Dispatch<BootAction>;
  submitRegister: () => Promise<void>;
  submitLogin: () => Promise<void>;
  logoutLocal: () => Promise<void>;
  handleRenameUsername: (next: string) => Promise<void>;
  setIdentityRow: React.Dispatch<React.SetStateAction<IdentityRow | null>>;
  setKeys: React.Dispatch<React.SetStateAction<IdentityKeys | null>>;
  /** Incremented after successful registration so Shell can open settings. */
  registerSuccessTick: number;
  /** Статус первой строки BootScreen (проверка обновлений PWA). */
  pwaBootUpdate: PwaBootUpdateUi;
  /** Режим оверлея: полный чеклист / только SW / короткий запуск после reload. */
  bootOverlayMode: BootOverlayMode;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { visualMode } = useTheme();
  const { trigger: triggerSessionReset } = useSessionResetRegistry();
  const [boot, bootDispatch] = useReducer(bootReducer, undefined, initialBootState);
  const [sodiumReady, setSodiumReady] = useState(false);
  const [identityRow, setIdentityRow] = useState<IdentityRow | null>(null);
  const [keys, setKeys] = useState<IdentityKeys | null>(null);
  const [authTab, setAuthTab] = useState<"register" | "login">("login");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPassword2, setRegPassword2] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regInviteCode, setRegInviteCode] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [bootError, setBootError] = useState<string | null>(null);
  const [renameUsernameError, setRenameUsernameError] = useState<string | null>(null);
  const [myAvatarUrls, setMyAvatarUrls] = useState<AvatarUrls | null>(null);
  const myAvatarUrl = useMemo(
    () => pickAvatarDisplayUrl(visualMode, myAvatarUrls),
    [visualMode, myAvatarUrls],
  );
  const [registerSuccessTick, setRegisterSuccessTick] = useState(0);

  const pwaBootUpdate = useSyncExternalStore(
    subscribePwaBootUpdate,
    getPwaBootUpdateSnapshot,
    getPwaBootUpdateSnapshot,
  );

  const bootOverlayMode = useSyncExternalStore(
    subscribeBootOverlayMode,
    getBootOverlayModeSnapshot,
    getBootOverlayModeSnapshot,
  );

  const keysRef = useRef<IdentityKeys | null>(null);
  const identityRowRef = useRef<IdentityRow | null>(null);
  const bootJitterTimersRef = useRef<Map<BootKey, number>>(new Map());

  const clearBootJitterTimers = useCallback(() => {
    for (const id of bootJitterTimersRef.current.values()) {
      window.clearTimeout(id);
    }
    bootJitterTimersRef.current.clear();
  }, []);

  const dispatchBoot = useCallback(
    (action: BootAction) => {
      if (action.type === "boot-reset" || action.type === "session-reset" || action.type === "boot-hide") {
        clearBootJitterTimers();
      }
      bootDispatch(action);
    },
    [clearBootJitterTimers],
  );

  useEffect(() => () => clearBootJitterTimers(), [clearBootJitterTimers]);

  useEffect(() => {
    keysRef.current = keys;
  }, [keys]);

  useEffect(() => {
    if (!keys) {
      clearLocalCacheKey();
      return;
    }
    setLocalCacheKeyFromSecretKey(keys.secretKeyX);
  }, [keys]);

  useEffect(() => {
    identityRowRef.current = identityRow;
  }, [identityRow]);

  const setBoot = useCallback((key: BootKey, nextState: BootItem["state"], detail?: string): Promise<void> => {
    const prev = bootJitterTimersRef.current.get(key);
    if (prev !== undefined) {
      window.clearTimeout(prev);
      bootJitterTimersRef.current.delete(key);
    }
    if (nextState === "done") {
      return new Promise((resolve) => {
        const jitter = isPostSwRestartBoot() ? 0 : bootDoneJitterMs();
        const id = window.setTimeout(() => {
          bootJitterTimersRef.current.delete(key);
          bootDispatch({ type: "boot-update", key, state: nextState, detail });
          resolve();
        }, jitter);
        bootJitterTimersRef.current.set(key, id);
      });
    }
    bootDispatch({ type: "boot-update", key, state: nextState, detail });
    return Promise.resolve();
  }, []);

  const hideBoot = useCallback(() => {
    const tryHide = () => {
      if (swUpdateBlocksBootDismiss()) {
        window.setTimeout(tryHide, 250);
        return;
      }
      dispatchBoot({ type: "boot-hide" });
      clearPostSwRestartBoot();
      setBootOverlayMode("full");
    };
    tryHide();
  }, [dispatchBoot]);

  useBootSequence({
    setBoot,
    onBootError: setBootError,
    setSodiumReady,
    setIdentityRow,
    setKeys,
    setLoginEmail,
    hideBoot,
  });

  const logoutLocal = useCallback(async () => {
    await unsubscribePushNotifications();
    if ("clearAppBadge" in navigator && typeof navigator.clearAppBadge === "function") {
      try {
        await navigator.clearAppBadge();
      } catch {
        /* ignore */
      }
    }
    triggerSessionReset();
    dispatchBoot({ type: "session-reset" });
    clearWorkspaceNavSessionCache();
    clearStoredJwt();
    try {
      await clearEncryptedLocalCaches();
    } catch {
      /* ignore */
    }
    clearLocalCacheKey();
    await stripIdentitySecret();
    setIdentityRow(null);
    setKeys(null);
    setRegUsername("");
    setRegEmail("");
    setRegPassword("");
    setRegPassword2("");
    setRegInviteCode("");
    setLoginEmail("");
    setLoginPassword("");
    setActiveUserId(null);
    setMyAvatarUrls(null);
  }, [dispatchBoot, triggerSessionReset]);

  const submitRegister = useCallback(async () => {
    setBootError(null);
    const email = regEmail.trim().toLowerCase();
    const username = normalizeUsername(regUsername);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setBootError("Укажите корректный email");
      return;
    }
    if (regPassword.length < 8) {
      setBootError("Пароль: минимум 8 символов");
      return;
    }
    if (regPassword !== regPassword2) {
      setBootError("Пароли не совпадают");
      return;
    }
    if (!username) {
      setBootError("Имя пользователя: a-z 0-9 _ 1–16");
      return;
    }
    if (!regInviteCode.trim()) {
      setBootError("Нужен инвайт-код");
      return;
    }

    try {
      dispatchBoot({ type: "boot-reset" });
      await setBoot("sodium", "done", "libsodium ready");
      await setBoot("vault", "active", "generating keys and registering");

      await initSodium();
      const identity = await generateIdentity(username);
      const keySalt = randomKeySalt();
      const encryptedKeys = await encryptSecretKeyBackup(identity.secretKeyEd, regPassword, keySalt);

      const res = await apiRegister({
        email,
        password: regPassword,
        username: identity.username,
        user_id: identity.userId,
        public_key_ed: u8ToB64(identity.publicKeyEd),
        public_key_x: u8ToB64(identity.publicKeyX),
        encrypted_keys: encryptedKeys,
        key_salt: u8ToB64(keySalt),
        invite_code: regInviteCode.trim(),
      });

      setStoredJwt(res.token);
      setActiveUserId(identity.userId);
      const row: IdentityRow = {
        userId: identity.userId,
        username: identity.username,
        email,
        publicKeyEdB64: u8ToB64(identity.publicKeyEd),
        secretKeyEdB64: u8ToB64(identity.secretKeyEd),
      };
      await saveIdentity(row);
      setIdentityRow(row);
      setKeys(identity);
      setRegisterSuccessTick((t) => t + 1);
      await setBoot("vault", "done", `account @${identity.username}`);
      await setBoot("format", "done", "magic bytes STXR");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setBootError(message);
      void setBoot("vault", "error", message);
    }
  }, [dispatchBoot, regEmail, regInviteCode, regPassword, regPassword2, regUsername, setBoot]);

  const submitLogin = useCallback(async () => {
    setBootError(null);
    const email = loginEmail.trim().toLowerCase();
    if (!email || loginPassword.length < 1) {
      setBootError("Email и пароль обязательны");
      return;
    }

    try {
      dispatchBoot({ type: "boot-reset" });
      await setBoot("sodium", "done", "libsodium ready");
      await setBoot("vault", "active", "signing in");

      await initSodium();
      const log = await apiLogin(email, loginPassword);
      consumeLoginThemeResponse(log.theme);
      setStoredJwt(log.token);
      setActiveUserId(log.user_id);

      const local = identityRowRef.current ?? (await loadIdentity()) ?? null;
      const sameUser = Boolean(local && local.userId === log.user_id);
      const hasLocalKeys = sameUser && Boolean(local!.secretKeyEdB64);

      let restoredKeys: IdentityKeys;
      if (hasLocalKeys) {
        restoredKeys = await identityFromStored(
          local!.userId,
          local!.username,
          local!.publicKeyEdB64,
          local!.secretKeyEdB64,
        );
      } else {
        const salt = b64ToU8(log.key_salt);
        const sk = await decryptSecretKeyBackup(log.encrypted_keys, loginPassword, salt);
        restoredKeys = await identityFromStored(log.user_id, log.username, log.public_key_ed, u8ToB64(sk));
      }

      const row: IdentityRow = {
        userId: log.user_id,
        username: log.username,
        email,
        publicKeyEdB64: u8ToB64(restoredKeys.publicKeyEd),
        secretKeyEdB64: u8ToB64(restoredKeys.secretKeyEd),
      };

      await saveIdentity(row);
      setIdentityRow(row);
      setKeys(restoredKeys);
      await setBoot("vault", "done", `signed in @${row.username}`);
      await setBoot("format", "done", "magic bytes STXR");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setBootError(message);
      void setBoot("vault", "error", message);
    }
  }, [dispatchBoot, loginEmail, loginPassword, setBoot]);

  const handleRenameUsername = useCallback(
    async (next: string) => {
      const n = normalizeUsername(next);
      if (!n) {
        setRenameUsernameError("Имя: a-z, 0-9, _, 1–16");
        throw new Error("INVALID_USERNAME");
      }
      if (!identityRow || !keys) {
        setRenameUsernameError("сессия не готова");
        throw new Error("no session");
      }
      try {
        await apiRenameUsername(n);
      } catch (e) {
        const raw = e instanceof Error ? e.message : String(e);
        let msg = raw;
        if (raw === "USERNAME_TAKEN") msg = "это имя уже занято";
        setRenameUsernameError(msg);
        throw e;
      }
      const row: IdentityRow = { ...identityRow, username: n };
      await saveIdentity(row);
      setIdentityRow(row);
      setKeys({ ...keys, username: n });
      setRenameUsernameError(null);
    },
    [identityRow, keys],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      sodiumReady,
      identityRow,
      keys,
      keysRef,
      identityRowRef,
      authTab,
      setAuthTab,
      regEmail,
      setRegEmail,
      regPassword,
      setRegPassword,
      regPassword2,
      setRegPassword2,
      regUsername,
      setRegUsername,
      regInviteCode,
      setRegInviteCode,
      loginEmail,
      setLoginEmail,
      loginPassword,
      setLoginPassword,
      bootError,
      setBootError,
      renameUsernameError,
      setRenameUsernameError,
      myAvatarUrl,
      myAvatarUrls,
      setMyAvatarUrls,
      bootItems: boot.bootItems,
      bootVisible: boot.bootVisible,
      setBoot,
      hideBoot,
      bootDispatch: dispatchBoot,
      submitRegister,
      submitLogin,
      logoutLocal,
      handleRenameUsername,
      setIdentityRow,
      setKeys,
      registerSuccessTick,
      pwaBootUpdate,
      bootOverlayMode,
    }),
    [
      sodiumReady,
      identityRow,
      keys,
      authTab,
      regEmail,
      regPassword,
      regPassword2,
      regUsername,
      regInviteCode,
      loginEmail,
      loginPassword,
      bootError,
      renameUsernameError,
      myAvatarUrl,
      myAvatarUrls,
      boot.bootItems,
      boot.bootVisible,
      setBoot,
      hideBoot,
      dispatchBoot,
      submitRegister,
      submitLogin,
      logoutLocal,
      handleRenameUsername,
      registerSuccessTick,
      pwaBootUpdate,
      bootOverlayMode,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
